/**
 * Hydrate + subscribe the user's conversation list.
 *
 *   const { conversations, hasMore, fetchMore, isLoading, error, refresh } =
 *     useConversationList()
 *
 * Pipeline:
 *   1. On first mount: REST-seed via `messengerApi.listConversations()` →
 *      store via `setWireConversationList(items, cursor, 'replace')`.
 *   2. `useMessengerSubscription` joins one Phoenix channel per visible
 *      conversation id so `appendWireMessage` / `message:deleted` / etc.
 *      keep the list fresh without polling.
 *   3. `fetchMore()` calls the API with the stored cursor and appends.
 *
 * Sort order mirrors the server (`lastMessageAt DESC`, fallback
 * `createdAt`) — store-side `sortConversationIds` keeps it consistent.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Conversation as WireConversation } from '../services/messenger.wire'
import {
  messengerApi,
  MessengerApiError,
} from '../services/messenger.api'
import { useMessengerStore } from '../store/messenger.store'
import { useMessengerSubscription } from './useMessengerSubscription'

interface UseConversationList {
  conversations: WireConversation[]
  hasMore:    boolean
  isLoading:  boolean
  error:      string | null
  fetchMore:  () => Promise<void>
  /** Re-seed from page 1 (e.g. after creating a new DM/group). */
  refresh:    () => Promise<void>
}

export function useConversationList(): UseConversationList {
  const order  = useMessengerStore((s) => s.wireConversationOrder)
  const byId   = useMessengerStore((s) => s.wireConversations)
  const cursor = useMessengerStore((s) => s.wireConversationListCursor)

  const setList = useMessengerStore((s) => s.setWireConversationList)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError]         = useState<string | null>(null)
  const seededRef = useRef<boolean>(false)

  // Initial REST seed — runs once. Re-mounts that race the StrictMode double
  // effect are guarded by `seededRef`.
  useEffect(() => {
    if (seededRef.current) return
    seededRef.current = true

    let cancelled = false
    setIsLoading(true)
    setError(null)
    messengerApi
      .listConversations({ limit: 50 })
      .then((page) => {
        if (cancelled) return
        setList(page.items, { nextCursor: page.nextCursor, hasMore: page.hasMore }, 'replace')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof MessengerApiError ? err.message : 'failed to load conversations'
        setError(message)
        // Reset so a manual refresh() can re-attempt.
        seededRef.current = false
      })
      .finally(() => { if (!cancelled) setIsLoading(false) })

    return () => { cancelled = true }
  }, [setList])

  // Subscribe to every conversation currently in the list so channel events
  // dispatch into the store. Memoized array of ids keeps the hook stable.
  const visibleIds = useMemo(() => order, [order])
  useMessengerSubscription(visibleIds)

  const fetchMore = useCallback(async (): Promise<void> => {
    if (!cursor.hasMore || !cursor.nextCursor || isLoading) return
    setIsLoading(true)
    setError(null)
    try {
      const page = await messengerApi.listConversations({
        limit: 50,
        before: cursor.nextCursor,
      })
      setList(page.items, { nextCursor: page.nextCursor, hasMore: page.hasMore }, 'append')
    } catch (err: unknown) {
      const message = err instanceof MessengerApiError ? err.message : 'failed to load more'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [cursor.hasMore, cursor.nextCursor, isLoading, setList])

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const page = await messengerApi.listConversations({ limit: 50 })
      setList(page.items, { nextCursor: page.nextCursor, hasMore: page.hasMore }, 'replace')
    } catch (err: unknown) {
      const message = err instanceof MessengerApiError ? err.message : 'failed to refresh'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [setList])

  // Project the Map → ordered array using the store's order array.
  const conversations = useMemo(
    () => order.map((id) => byId.get(id)).filter((c): c is WireConversation => c !== undefined),
    [order, byId],
  )

  return {
    conversations,
    hasMore: cursor.hasMore,
    isLoading,
    error,
    fetchMore,
    refresh,
  }
}
